import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import { money } from "@/lib/api";
import { X, Plus, Minus, Trash, ArrowRight, ShoppingBag } from "@phosphor-icons/react";

export default function MiniCartDrawer({ open, onClose }) {
  const { items, update, remove, subtotal_cents } = useCart();
  const navigate = useNavigate();

  // Lock body scroll while open + close on Esc
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`}
      data-testid="mini-cart"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      {/* Panel */}
      <aside
        className={`absolute top-0 right-0 h-full w-full sm:w-[420px] bg-[color:var(--prmu-bg,#0a0a0b)] border-l border-white/10 shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} weight="duotone" className="text-blue-400" />
            <div className="font-display text-lg tracking-tight">Your cart</div>
            <span className="ml-1 chip">{items.length}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full border border-white/10 hover:border-white/40"
            data-testid="mini-cart-close"
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="mini-cart-body">
          {items.length === 0 ? (
            <div className="text-center py-14">
              <div className="overline mb-3">Cart is empty</div>
              <p className="text-sm text-zinc-500 mb-6">Nothing here yet — start with a best seller.</p>
              <button
                onClick={() => go("/shop")}
                className="btn-prm !text-xs"
                data-testid="mini-cart-shop"
              >
                Browse catalog <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          ) : (
            items.map((it) => (
              <div key={it.key} className="flex gap-3 border-b border-white/10 pb-4 last:border-0" data-testid={`mini-cart-item-${it.product_slug}`}>
                <Link to={`/product/${it.product_slug}`} onClick={onClose} className="shrink-0">
                  <div className="w-16 h-20 rounded-lg overflow-hidden bg-black/30 border border-white/10">
                    {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.product_slug}`} onClick={onClose} className="block">
                    <div className="font-display-plain font-semibold text-sm truncate hover:text-blue-400 transition-colors">
                      {it.product_name}
                    </div>
                  </Link>
                  {it.variant_label && (
                    <div className="text-xs font-mono text-zinc-500 mt-0.5">{it.variant_label}</div>
                  )}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="inline-flex items-center border border-white/15 rounded-full">
                      <button
                        onClick={() => (it.quantity === 1 ? remove(it.key) : update(it.key, it.quantity - 1))}
                        className="px-2 py-1 hover:text-blue-400"
                        data-testid={`mini-dec-${it.product_slug}`}
                        aria-label="Decrease"
                      >
                        <Minus size={12} weight="bold" />
                      </button>
                      <span className="w-6 text-center font-mono text-xs">{it.quantity}</span>
                      <button
                        onClick={() => update(it.key, Math.min(99, it.quantity + 1))}
                        className="px-2 py-1 hover:text-blue-400"
                        data-testid={`mini-inc-${it.product_slug}`}
                        aria-label="Increase"
                      >
                        <Plus size={12} weight="bold" />
                      </button>
                    </div>
                    <div className="font-mono text-sm">{money(it.unit_price_cents * it.quantity)}</div>
                    <button
                      onClick={() => remove(it.key)}
                      className="p-1 text-zinc-500 hover:text-red-400"
                      data-testid={`mini-remove-${it.product_slug}`}
                      aria-label="Remove"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 px-5 py-4 space-y-3 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <div className="overline">Subtotal</div>
              <div className="font-display text-2xl font-bold" data-testid="mini-cart-subtotal">
                {money(subtotal_cents)}
              </div>
            </div>
            <div className="text-xs text-zinc-500">Shipping and taxes calculated at checkout.</div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={() => go("/cart")}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-white/15 hover:border-white/40 text-sm"
                data-testid="mini-cart-view"
              >
                View cart
              </button>
              <button
                onClick={() => go("/checkout")}
                className="btn-prm !py-2.5 !text-xs"
                data-testid="mini-cart-checkout"
              >
                Checkout <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
